import { Component } from '@theme/component';

const COPIED_RESET_MS = 3000;

/**
 * @typedef {Object} DiscountCodeCopyRefs
 * @property {HTMLElement} defaultState
 * @property {HTMLElement} successState
 * @property {HTMLButtonElement} copyButton
 */

/**
 * Copy discount code and toggle the button to a success state.
 * @extends {Component<DiscountCodeCopyRefs>}
 */
class DiscountCodeCopy extends Component {
  /** @type {number | undefined} */
  #resetTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#resetCopiedState();
  }

  /**
   * Copy the discount code and show the copied state.
   * @param {Event} [event]
   */
  async copyCode(event) {
    event?.preventDefault();

    const code = this.getAttribute('text-to-copy');
    if (!code || this.classList.contains('discount-code__copy--copied')) return;

    const copied = await this.#writeTextToClipboard(code);
    if (!copied) return;

    this.#showCopiedState();
  }

  #showCopiedState() {
    const { successState, copyButton } = this.refs;

    if (!(successState instanceof HTMLElement)) return;

    this.classList.add('discount-code__copy--copied');
    successState.setAttribute('aria-hidden', 'false');

    if (copyButton instanceof HTMLButtonElement) {
      copyButton.setAttribute('aria-disabled', 'true');
    }

    clearTimeout(this.#resetTimer);
    this.#resetTimer = window.setTimeout(() => {
      this.#resetCopiedState();
    }, COPIED_RESET_MS);
  }

  #resetCopiedState() {
    const { successState, copyButton } = this.refs;

    this.classList.remove('discount-code__copy--copied');

    if (successState instanceof HTMLElement) {
      successState.setAttribute('aria-hidden', 'true');
    }

    if (copyButton instanceof HTMLButtonElement) {
      copyButton.removeAttribute('aria-disabled');
    }
  }

  /**
   * @param {string} text
   * @returns {Promise<boolean>}
   */
  async #writeTextToClipboard(text) {
    if (navigator.clipboard?.writeText && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        // Fall through to legacy copy fallback.
      }
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();

    let copied = false;

    try {
      copied = document.execCommand('copy');
    } catch {
      copied = false;
    } finally {
      textarea.remove();
    }

    return copied;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#resetTimer);
  }
}

if (!customElements.get('discount-code-copy')) {
  customElements.define('discount-code-copy', DiscountCodeCopy);
}
